import { useMemo } from 'react';
import {
  useFindManyIssuersQuery,
  IssuerFilteringField,
  FilteringConnector,
  FilteringType,
} from '../types/graphql';

/**
 * Fetches issuer logos for a list of issuer UUIDs.
 *
 * Returns a map of issuerUuid → base64 logo string.
 * Skips the query entirely when the UUID list is empty.
 */
export function useIssuerLogos(issuerUuids: string[]): {
  logos: Record<string, string>;
  loading: boolean;
} {
  const skip = issuerUuids.length === 0;

  const { data, loading } = useFindManyIssuersQuery({
    skip,
    variables: {
      input: {
        filtering: issuerUuids.map((uuid) => ({
          connector: FilteringConnector.Disjunction,
          field: IssuerFilteringField.Uuid,
          value: uuid,
          type: FilteringType.Exact,
        })),
        pagination: { first: 100 },
      },
    },
  });

  const logos = useMemo(() => {
    if (!data) return {};

    const map: Record<string, string> = {};
    for (const edge of data.findManyIssuers.edges) {
      const node = edge?.node;
      if (!node?.base64Logo) continue;
      map[node.uuid] = node.base64Logo;
    }
    return map;
  }, [data]);

  return { logos, loading };
}
